import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Clear";
import { CircularProgress, InputAdornment } from "@mui/material";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import PropTypes from "prop-types";
import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import {
  ALL_ACCESS_LEVELS,
  CANCEL_WARNING_MESSAGE_ACTION_TYPE,
  MULTI_STATION_ADMIN_ACCESS,
  EMPLOYEE_TEMP_LABEL,
} from "../util/constants";
import {
  ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE,
  ARE_YOU_SURE_YOU_WANT_TO_SAVE_YOUR_CHANGES_MESSAGE,
  ARE_YOU_SURE_YOU_WANT_TO_REMOVE_ACCESS_DIALOG_MESSAGE,
  ACCESS_EXCEPTION_NO_LONGER_VALID_TEMP_ASSIGNMENT_ERROR,
  ACCESS_EXCEPTION_OUTGOING_TEMP_ASSIGNMENT_ERROR,
  REMOVE_EXCEPTION,
} from "../util/messageConstants";
import { makeStyles } from "@mui/styles";
import ConfirmationDialog from "./fundamentals/ConfirmationDialog";
import SlideUpTransition from "./fundamentals/SlideUpTransition";
import { getWorkgroupGivenJobCode } from "../util/workgroupMapper.js";

const useStyles = makeStyles((theme) => ({
  textField: {
    width: "168px",
    maxWidth: "95vw",
    margin: "auto",
  },
  tempChip: {
    marginLeft: "5px",
  },
  underlineText: {
    textDecoration: "underline",
  },
}));

const ManageAccessDialog = (props) => {
  const [isAreYouSureDialogOpen, setIsAreYouSureDialogOpen] = useState(false);
  const [confirmQuestion, setConfirmQuestion] = useState("");
  const [isCancelClicked, setIsCancelClicked] = useState(true);
  const [isRemoveClicked, setIsRemoveClicked] = useState(false);

  const isEmployeeDataEmpty = () => {
    return !props.employeeFirstName || !props.employeeLastName || !props.employeeWorkgroup;
  };
  const classes = useStyles();
  const handleClickDialogClose = (event, reason) => {
    props.onClickDialogClose(event, reason);
  };

  const handleEmployeeIdChange = (event) => {
    props.onHandleEmployeeIdChange(event);
  };

  const handleEmployeeIdBlur = (event) => {
    props.onHandleEmployeeIdBlur(event);
  };

  const handleEmployeeIdEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleEmployeeIdBlur(event);
    }
  };

  const handleSelectedAccessLevelChange = (event) => {
    props.onHandleSelectedAccessLevelChange(event);
  };

  const handleCurrentStationChange = (event) => {
    props.onHandleCurrentStationChange(event);
  };

  const handleAddStationClick = () => {
    props.onHandleAddStationClick();
  };

  const handleAddStationEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleAddStationClick();
    }
  };

  const handleRemoveStationClick = (station) => {
    props.onHandleRemoveStationClick(station);
  };

  const renderAccessLevelMenuItems = () => {
    return ALL_ACCESS_LEVELS.slice()
      .sort()
      .map((accessLevel) => {
        return (
          <MenuItem key={accessLevel} value={accessLevel}>
            {accessLevel}
          </MenuItem>
        );
      });
  };

  const renderMultiStationAdminStationListChips = () => {
    return props.multiStationAdminStationList.map((station) => {
      if (station === props.selectedSuperuserStation) {
        return <Chip key={station} className="station-chip" label={station} />;
      } else {
        return (
          <Chip
            key={station}
            className="station-chip"
            label={station}
            onDelete={handleRemoveStationClick.bind(this, station)}
          />
        );
      }
    });
  };

  const handleClickAreYouSureDialogOpen = (event) => {
    const isCancel = event.currentTarget.id === "cancelButton";
    const isRemoveAccessButton = event.currentTarget.id === "removeAccessButton";
    setConfirmQuestion(
      isCancel && !props.isEdit
        ? ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.DATA)
        : isCancel && props.isEdit
        ? ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.EDITS)
        : isRemoveAccessButton && props.isEdit
        ? ARE_YOU_SURE_YOU_WANT_TO_REMOVE_ACCESS_DIALOG_MESSAGE
        : ARE_YOU_SURE_YOU_WANT_TO_SAVE_YOUR_CHANGES_MESSAGE
    );
    if (isRemoveAccessButton) {
      setIsRemoveClicked(isRemoveAccessButton);
      props.setDialogEditIsSetToDefault(true);
    } else {
      setIsCancelClicked(isCancel);
      setIsRemoveClicked(false);
    }
    //This is to validate the form before asking for confirmation if action is save
    if (!isCancel && !isRemoveAccessButton && !props.formValidationSuccess()) {
      return;
    }
    setIsAreYouSureDialogOpen(true);
  };

  const handleNoClickedAreYouSureDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsAreYouSureDialogOpen(false);
  };

  const handleYesClickedAreYouSureDialogClose = (event) => {
    if (!isCancelClicked) {
      props.onSubmitAccessOverrideEvent(event);
    } else if (isRemoveClicked && isCancelClicked) {
      props.onSetToDefaultClick();
      props.onSubmitAccessOverrideEvent(event);
    } else {
      handleClickDialogClose(event);
    }
    setIsAreYouSureDialogOpen(false);
  };

  const CancelButtonEmployee = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={props.onHandleEmployeeClear}>
        <CancelIcon />
      </IconButton>
    );
  };
  const isOutgoingTempAccessException =
    !!props.employeeTempStation && props.employeeTempStation !== props.selectedSuperuserStation;
  const isInvalidTempAccessException =
    !props.employeeTempStation &&
    props.isEdit &&
    (props.selectedSuperuserStation !== props.employeeHomeStation ||
      props.employeeJobCode !== props.accessOverrideEvent.jobCode);
  return (
    <Dialog
      className="manage-access-dialog"
      fullWidth
      maxWidth="md"
      open={props.isDialogOpen}
      TransitionComponent={SlideUpTransition}
      keepMounted
      onClose={handleClickDialogClose.bind(this)}
      aria-labelledby="alert-dialog-slide-title"
      aria-describedby="alert-dialog-slide-description"
      data-testid="manage-access-dialog"
    >
      <DialogTitle id="manage-access-dialog-slide-title" className="manage-access-dialog-section-title">
        Manage Access
      </DialogTitle>
      {props.isEdit && isOutgoingTempAccessException && (
        <Grid container justifyContent="center">
          <Box width={400}>
            <Alert severity="error">{ACCESS_EXCEPTION_OUTGOING_TEMP_ASSIGNMENT_ERROR}</Alert>
          </Box>
        </Grid>
      )}
      {isInvalidTempAccessException
        ? props.employeeSearchTriggered &&
          props.employeeSearchLoaded &&
          props.isEdit && (
            <Grid container justifyContent="center">
              <Box width={400}>
                <Alert severity="error">{ACCESS_EXCEPTION_NO_LONGER_VALID_TEMP_ASSIGNMENT_ERROR}</Alert>
              </Box>
            </Grid>
          )
        : ""}
      <DialogContent className="manage-access-dialog-content">
        <DialogContentText component={"div"} id="manage-access-dialog-slide-description">
          <Grid container justifyContent="center">
            <Grid container justifyContent="center" spacing={2} xs={12}>
              <Grid item>
                <Box mt={1} mb={1}>
                  <TextField
                    className={classes.textField}
                    label="Superuser Station"
                    variant="outlined"
                    value={props.selectedSuperuserStation}
                    disabled
                    error={props.stationErrors !== ""}
                    helperText={props.stationErrors}
                  />
                </Box>
              </Grid>
              <Grid item>
                <Box mt={1} mb={1}>
                  {props.isEdit ? (
                    <TextField
                      className={classes.textField}
                      label="Employee ID"
                      variant="outlined"
                      value={props.employeeId}
                      onChange={handleEmployeeIdChange}
                      onBlur={handleEmployeeIdBlur}
                      onKeyUp={handleEmployeeIdEnterKeyPressed}
                      error={props.employeeIdErrors !== ""}
                      helperText={props.employeeIdErrors}
                      disabled
                    />
                  ) : (
                    <TextField
                      className={classes.textField}
                      label="Employee ID"
                      variant="outlined"
                      value={props.employeeId}
                      onChange={handleEmployeeIdChange}
                      onBlur={handleEmployeeIdBlur}
                      onKeyUp={handleEmployeeIdEnterKeyPressed}
                      error={props.employeeIdErrors !== ""}
                      InputProps={{
                        endAdornment:
                          props.employeeId !== "" ? (
                            <InputAdornment position="start">
                              <CancelButtonEmployee />
                            </InputAdornment>
                          ) : (
                            ""
                          ),
                      }}
                      helperText={props.employeeIdErrors}
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
            {props.employeeSearchTriggered ? (
              props.employeeSearchLoaded ? (
                isEmployeeDataEmpty() || (!props.isEdit && props.employeeIdErrors !== "") ? (
                  ""
                ) : (
                  <Grid container className="manage-access-employee-text" xs={12}>
                    <Grid container xs={12}>
                      <div style={{ marginTop: 5 }}>
                        <span className="bold-text">Name:</span> {props.employeeLastName}, {props.employeeFirstName}
                      </div>
                      {props.employeeTempStation && <Chip className={classes.tempChip} label={EMPLOYEE_TEMP_LABEL} />}
                    </Grid>
                    {isOutgoingTempAccessException ? (
                      <Grid container spacing={3} xs={12}>
                        <Grid item xs={6}>
                          <Typography className={classes.underlineText}>
                            <span className="bold-text">Previous</span>
                          </Typography>
                          <div>
                            <span className="bold-text">Station:</span>{" "}
                            {props.accessOverrideEvent.station}
                          </div>
                          <div>
                            <span className="bold-text">Workgroup:</span>{" "}
                            {getWorkgroupGivenJobCode(props.accessOverrideEvent.jobCode)}
                          </div>
                          <div>
                            <span className="bold-text">Job Code:</span>{" "}
                            {props.accessOverrideEvent.jobCode}
                          </div>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography className={classes.underlineText}>
                            <span className="bold-text">Current</span>
                          </Typography>
                          <div>
                            <span className="bold-text">Station:</span> {props.employeeTempStation}
                          </div>
                          <div>
                            <span className="bold-text">Workgroup:</span> {props.employeeWorkgroup}
                          </div>
                          <div>
                            <span className="bold-text">Job Code:</span> {props.dialogEmployeeJobCode}
                          </div>
                        </Grid>
                      </Grid>
                    ) : isInvalidTempAccessException ? (
                      <Grid container spacing={3} xs={12}>
                        <Grid item xs={6}>
                          <Typography className={classes.underlineText}>
                            <span className="bold-text">Previous</span>
                          </Typography>
                          <div>
                            <span className="bold-text">Station:</span> {props.accessOverrideEvent.station}
                          </div>
                          <div>
                            <span className="bold-text">Workgroup:</span>{" "}
                            {getWorkgroupGivenJobCode(props.accessOverrideEvent.jobCode)}
                          </div>
                          <div>
                            <span className="bold-text">Job Code:</span> {props.accessOverrideEvent.jobCode}
                          </div>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography className={classes.underlineText}>
                            <span className="bold-text">Current</span>
                          </Typography>
                          <div>
                            <span className="bold-text">Station:</span> {props.employeeActiveStation}
                          </div>
                          <div>
                            <span className="bold-text">Workgroup:</span> {props.employeeWorkgroup}
                          </div>
                          <div>
                            <span className="bold-text">Job Code:</span> {props.dialogEmployeeJobCode}
                          </div>
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid>
                        <div>
                          <span className="bold-text">Station:</span> {props.employeeActiveStation}
                        </div>
                        <div>
                          <span className="bold-text">Workgroup:</span> {props.employeeWorkgroup}
                        </div>
                        <div>
                          <span className="bold-text">Job Code:</span> {props.dialogEmployeeJobCode}
                        </div>
                      </Grid>
                    )}
                  </Grid>
                )
              ) : (
                <Grid className="manage-access-employee-text" item xs={12}>
                  <CircularProgress />
                </Grid>
              )
            ) : (
              ""
            )}
            <Grid item xs={12}>
              <Box mt={2} mb={1}>
                <FormControl variant="outlined">
                  <InputLabel id="selectedStationSelectLabel" error={props.selectedAccessLevelErrors !== ""}>
                    Access Level
                  </InputLabel>
                  <Select
                    className="select-field"
                    labelId="selectedStationSelectLabel"
                    id="selectedStationSelectElement"
                    value={props.selectedAccessLevel}
                    onChange={handleSelectedAccessLevelChange.bind(this)}
                    label="Access Level"
                    error={props.selectedAccessLevelErrors !== ""}
                  >
                    <MenuItem value="" disabled>
                      <em>Access Level</em>
                    </MenuItem>
                    {renderAccessLevelMenuItems()}
                  </Select>
                  {props.selectedAccessLevelErrors !== "" && (
                    <FormHelperText error>{props.selectedAccessLevelErrors}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Grid>
            {props.selectedAccessLevel === MULTI_STATION_ADMIN_ACCESS ? (
              <Grid container className="multi-station-list-edit-section">
                <Grid item xs={8}>
                  <Box mt={1} mb={1}>
                    <TextField
                      className="manage-access-dialog-station-text"
                      label="Admin Station"
                      variant="outlined"
                      value={props.currentStation}
                      onChange={handleCurrentStationChange.bind(this)}
                      onKeyUp={handleAddStationEnterKeyPressed.bind(this)}
                      error={props.currentStationErrors !== ""}
                      helperText={props.currentStationErrors}
                    />
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box mt={1} mb={1}>
                    <Button
                      className="manage-access-dialog-station-button"
                      variant="contained"
                      color="primary"
                      onClick={handleAddStationClick.bind(this)}
                    >
                      <AddIcon className="button-icon" />
                      <p className="new-access-button-text">ADD</p>
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box mt={1} mb={1} className="manage-access-dialog-station-chips">
                    {renderMultiStationAdminStationListChips()}
                  </Box>
                </Grid>
              </Grid>
            ) : (
              ""
            )}
            {!props.isInitialEmployeeAccessLevelLoad &&
            !isOutgoingTempAccessException &&
            !isInvalidTempAccessException &&
            props.isEditSetToDefault ? (
              <Grid container className="multi-station-list-edit-section">
                <Grid item xs={12}>
                  <Alert severity="warning" className="text-field">
                    <AlertTitle>
                      <strong>WARNING:</strong>
                    </AlertTitle>
                    You are setting this User’s access back to default. The User will not appear in the Access
                    Exceptions list.
                  </Alert>
                </Grid>
              </Grid>
            ) : (
              ""
            )}
            {props.isSaveError ? (
              <Grid container className="multi-station-list-edit-section">
                <Grid item xs={12}>
                  <Alert severity="error" className="text-field">
                    <AlertTitle>
                      <strong>SAVE ERROR:</strong>
                    </AlertTitle>
                    {props.saveErrors}
                  </Alert>
                </Grid>
              </Grid>
            ) : (
              ""
            )}
          </Grid>
        </DialogContentText>
      </DialogContent>
      <DialogActions className="manage-access-dialog-buttons">
        {props.isEdit && props.employeeSearchTriggered && props.employeeSearchLoaded ? (
          isOutgoingTempAccessException || isInvalidTempAccessException ? (
            <Button
              id="removeAccessButton"
              className="dialog-left-button"
              variant="outlined"
              color="error"
              onClick={handleClickAreYouSureDialogOpen}
            >
              {REMOVE_EXCEPTION}
            </Button>
          ) : (
            (props.employeeIdErrors === "" || props.isEdit) &&
            !props.isEditSetToDefault && (
              <Button
                className="dialog-left-button"
                variant="outlined"
                color="primary"
                onClick={props.onSetToDefaultClick}
              >
                Set To Default
              </Button>
            )
          )
        ) : null}

        {props.isSaveTriggered ? (
          props.isSaveLoading ? (
            <Button className="dialog-right-button" variant="contained" color="primary" disabled>
              <CircularProgress size={24} />
            </Button>
          ) : (
            <Button
              id="saveButton"
              className="dialog-right-button"
              variant="contained"
              color="primary"
              onClick={handleClickAreYouSureDialogOpen}
              disabled={
                isOutgoingTempAccessException || isInvalidTempAccessException
                  ? true
                  : props.employeeIdErrors !== ""
                  ? !props.isEditSetToDefault
                  : false
              }
            >
              Save
            </Button>
          )
        ) : (
          <Button
            id="saveButton"
            className="dialog-right-button"
            variant="contained"
            color="primary"
            onClick={handleClickAreYouSureDialogOpen}
            disabled={
              isOutgoingTempAccessException || isInvalidTempAccessException
                ? true
                : props.employeeIdErrors !== ""
                ? !props.isEditSetToDefault
                : false
            }
          >
            Save
          </Button>
        )}

        <Button
          id="cancelButton"
          className="dialog-right-button"
          variant="outlined"
          color="primary"
          onClick={handleClickAreYouSureDialogOpen}
        >
          Cancel
        </Button>
      </DialogActions>
      {isAreYouSureDialogOpen && (
        <ConfirmationDialog
          isDialogOpen={isAreYouSureDialogOpen}
          message={confirmQuestion}
          handleNoButtonClicked={handleNoClickedAreYouSureDialogClose}
          handleYesButtonClicked={handleYesClickedAreYouSureDialogClose}
        />
      )}
    </Dialog>
  );
};

ManageAccessDialog.propTypes = {
  selectedSuperuserStation: PropTypes.string.isRequired,
  stationErrors: PropTypes.string.isRequired,
  accessOverrideEvent: PropTypes.object.isRequired,
  employeeId: PropTypes.string.isRequired,
  employeeSearchTriggered: PropTypes.bool.isRequired,
  employeeSearchLoaded: PropTypes.bool.isRequired,
  employeeIdErrors: PropTypes.string.isRequired,
  onClickDialogClose: PropTypes.func.isRequired,
  onHandleEmployeeIdChange: PropTypes.func.isRequired,
  employeeFirstName: PropTypes.string.isRequired,
  employeeLastName: PropTypes.string.isRequired,
  employeeWorkgroup: PropTypes.string.isRequired,
  selectedAccessLevel: PropTypes.string.isRequired,
  selectedAccessLevelErrors: PropTypes.string.isRequired,
  currentStation: PropTypes.string.isRequired,
  currentStationErrors: PropTypes.string.isRequired,
  multiStationAdminStationList: PropTypes.array.isRequired,
  isInitialEmployeeAccessLevelLoad: PropTypes.bool.isRequired,
  isEdit: PropTypes.bool.isRequired,
  isEditSetToDefault: PropTypes.bool.isRequired,
  isDialogOpen: PropTypes.bool.isRequired,
  isSaveError: PropTypes.bool.isRequired,
  saveErrors: PropTypes.string.isRequired,
  isSaveTriggered: PropTypes.bool.isRequired,
  isSaveLoading: PropTypes.bool.isRequired,
  formValidationSuccess: PropTypes.func.isRequired,
  onSetToDefaultClick: PropTypes.func.isRequired,
  onHandleEmployeeIdBlur: PropTypes.func.isRequired,
  onHandleSelectedAccessLevelChange: PropTypes.func.isRequired,
  onHandleCurrentStationChange: PropTypes.func.isRequired,
  onHandleAddStationClick: PropTypes.func.isRequired,
  onHandleRemoveStationClick: PropTypes.func.isRequired,
  onSubmitAccessOverrideEvent: PropTypes.func.isRequired,
  onHandleEmployeeClear: PropTypes.func.isRequired,
  dialogEmployeeJobCode: PropTypes.string.isRequired,
  setDialogEditIsSetToDefault: PropTypes.func.isRequired,
  setIsRemoveAccessButtonClicked: PropTypes.func.isRequired,
};

export default ManageAccessDialog;
